import { EntityLayout } from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useMemo } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useSelector } from 'react-redux';

import TeamOverviewPage from '../../../pages/GroupPage/Members/TeamOverviewPage';
import { techdocsContent } from '../content/techdocsContent';
import { GroupGuardrailComplianceCard, GroupComplianceDetails } from '@internal/backstage-backstage-plugin-guardrail-compliance';
import { CustomDomainEntityLayout } from '../../../pages/EntityLayout/CustomDomainEntityLayout';
import GraphCard from '../../EntityCatalogGraphCard';

type GroupPageProps = {
  entityWarningContent: JSX.Element;
};

const useStyles = makeStyles(theme => ({
  warningIcon: {
    color: theme.palette.warning.main,
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
  successIcon: {
    color: theme.palette.success.main,
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
  },
}));

const guardrailComplianceTabTeamTopologies = new Set([
  'product team',
  'platform team',
  'subsystem team',
  'art leadership',
  'support team',
  'enabling team',
  'core functions',
  'board',
  'coe core team',
  'vs leadership',
]);

export const GroupPage = ({ entityWarningContent }: GroupPageProps) => {
  const { entity } = useEntity();
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const groupInfoArr = useSelector((state: any) => state.groupInfo.groupInfoArr);

  const hasGuardrailComplianceTabTopology = useMemo(() => {
    const groupId = entity.metadata.annotations?.['graph.microsoft.com/group-id'];
    if (!groupId || !Array.isArray(groupInfoArr) || groupInfoArr.length === 0) {
      return false;
    }

    const groupInfoData = groupInfoArr.find(
      (x: { unprocessed_entity: string }) => {
        try {
          const unprocessedEntity = JSON.parse(x?.unprocessed_entity);
          const entraGroupObjectId = unprocessedEntity?.spec?.entraGroupObjectId;
          return (
            typeof entraGroupObjectId === 'string' &&
            entraGroupObjectId.toLowerCase() === groupId.toLowerCase()
          );
        } catch {
          return false;
        }
      },
    );

    if (!groupInfoData?.unprocessed_entity) {
      return false;
    }

    try {
      const groupInfo = JSON.parse(groupInfoData.unprocessed_entity);
      const teamTopology = groupInfo?.spec?.teamTopology;
      if (typeof teamTopology !== 'string') {
        return false;
      }

      const normalizedTopology = teamTopology.toLowerCase().trim();
      return guardrailComplianceTabTeamTopologies.has(normalizedTopology);
    } catch {
      return false;
    }
  }, [entity.metadata.annotations, groupInfoArr]);

  const showGuardrailComplianceTab = hasGuardrailComplianceTabTopology;

  // Extract just the group name for the simplified API endpoint
  const groupName = entity.metadata.name;

  const { value: complianceData } = useAsync(
    async (): Promise<GroupComplianceDetails | null> => {
      if (!showGuardrailComplianceTab) {
        return null;
      }
      try {
        const baseUrl = await discoveryApi.getBaseUrl('guardrail-compliance');
        const response = await fetch(
          `${baseUrl}/v1/compliance/${encodeURIComponent(groupName)}`,
        );
        if (!response.ok) {
          return null;
        }
        return response.json();
      } catch {
        return null;
      }
    },
    [discoveryApi, fetch, groupName, showGuardrailComplianceTab],
  );

  const isFullyCompliant = complianceData?.compliancePercentage === 100;
  const hasComplianceData = complianceData !== null;

  return (
    <CustomDomainEntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3}>
          {entityWarningContent}
          <Grid item md={12} xs={12}>
            <TeamOverviewPage />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      {showGuardrailComplianceTab && (
        <EntityLayout.Route path="/compliance" title="Guardrails Compliance" tabProps={{
          label: (
            <span className={classes.tabLabel}>
              Guardrails Compliance
              {hasComplianceData && !isFullyCompliant && (
                <WarningIcon className={classes.warningIcon} style={{ fontSize: '1.25rem' }} />
              )}
              {hasComplianceData && isFullyCompliant && (
                <CheckCircleIcon className={classes.successIcon} style={{ fontSize: '1.25rem' }} />
              )}
            </span>
          ),
        }}>
          <Grid container spacing={3}>
            <Grid item md={12} xs={12}>
              <GroupGuardrailComplianceCard />
            </Grid>
          </Grid>
        </EntityLayout.Route>
      )}
      {isTechDocsAvailable(entity) && (
        <EntityLayout.Route path="/docs" title="Docs">
          {techdocsContent}
        </EntityLayout.Route>
      )}
      <EntityLayout.Route path="/graph" title="Graph">
        <Grid container spacing={3}>
          <Grid item md={12} xs={12}>
            <GraphCard isDefault={false} height={600} />
          </Grid>
        </Grid>
      </EntityLayout.Route>
    </CustomDomainEntityLayout>
  );
};
